// ✅ src/pages/VehicleV2/BatteryCardMaintenanceReport.jsx
// UPDATED — Production BOM Listing design template
// Overflow fixed + Search Criteria in 3 columns × 4 rows

import React, {
  useEffect,
  useMemo,
  useState,
  useContext,
  useRef,
  useCallback,
} from "react";
import axios from "axios";
import {
  FaSearch,
  FaPrint,
  FaArrowLeft,
  FaFileExport,
  FaSyncAlt,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

const API_BASE = "https://nissi-india.com/api/batteryCardMaintenanceReport";

/* ---------------- helpers ---------------- */
const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};

const toStr = (v, def = "") =>
  v === null || v === undefined ? def : String(v).trim();

const toList = (arr) => (Array.isArray(arr) ? arr : []);

const fmtMoney = (v) => {
  const n = Number(String(v ?? "").replace(/,/g, ""));
  if (!Number.isFinite(n)) return v ?? "";
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

const fmtDate = (v) => (v ? String(v) : "");

const normalizeOptionList = (arr, fallbackLabel = "Select", fallbackValue = "Select") => {
  const list = toList(arr).map((x) => {
    if (typeof x === "string" || typeof x === "number") {
      return { label: String(x), value: String(x) };
    }

    return {
      label: toStr(x.label ?? x.name ?? x.text ?? fallbackLabel, fallbackLabel),
      value: String(x.value ?? x.id ?? x.key ?? fallbackValue),
      ...x,
    };
  });

  return list.length ? list : [{ label: fallbackLabel, value: fallbackValue }];
};

const createDefaultFilters = (branchType, branchId) => ({
  fromDate: "",
  toDate: "",
  selectedBranchId: branchType === "HO" ? "Select" : String(branchId),

  vehicleCode: "",
  vehicleRegNo: "",
  vehicleName: "",

  batteryCode: "Select",
  batteryType: "Select",
  batterySerialNo: "Select",

  batteryStatus: "Select",
  vehicleMainTypeIndex: "Select",
  vehicleStatus: "Select",

  store: "Select",
});

export default function BatteryCardMaintenanceReport() {
  const navigate = useNavigate();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  /* ===========================
     shared local/session pattern
  =========================== */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";

  const branchIdRaw =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "8";

  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";

  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";

  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";
  const sessionBranchId = toInt(branchIdRaw, 8);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": String(sessionBranchId),
      "x-branch": String(branchType),
      "x-userid": String(loginUserId),
    }),
    [finYearId, finYear, sessionBranchId, branchType, loginUserId]
  );

  const session = useMemo(
    () => ({
      branchId: sessionBranchId,
      branchType: toStr(branchType || "HO"),
      loginUserId: toInt(loginUserId, 0),
    }),
    [sessionBranchId, branchType, loginUserId]
  );

  const defaultFilters = useMemo(
    () => createDefaultFilters(session.branchType, session.branchId),
    [session.branchType, session.branchId]
  );

  /* ===========================
     refs
  =========================== */
  const didInitRef = useRef(false);

  /* ===========================
     loading states
  =========================== */
  const [pageLoading, setPageLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);

  const isBusy = pageLoading || dropdownLoading || listLoading;

  /* ===========================
     UI states
  =========================== */
  const [showFilters, setShowFilters] = useState(true);

  /* ===========================
     meta/dropdown states
  =========================== */
  const [meta, setMeta] = useState({
    branches: [{ value: "Select", label: "Select" }],
    batteryCodes: [{ value: "Select", label: "Select" }],
    batteryTypes: [{ value: "Select", label: "Select" }],
    serialNos: [{ value: "Select", label: "Select" }],
    vehicleMainTypes: [{ value: "Select", label: "Select" }],
    batteryStatuses: [{ value: "Select", label: "Select" }],
    vehicleStatuses: [{ value: "Select", label: "Select" }],
  });

  /* ===========================
     filters + list states
  =========================== */
  const [filters, setFilters] = useState(defaultFilters);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);

  /* ===========================
     computed
  =========================== */
  const totalRows = rows.length;

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalRows / pageSize)),
    [totalRows, pageSize]
  );

  const indexOfLastItem = page * pageSize;
  const indexOfFirstItem = indexOfLastItem - pageSize;

  const currentItems = useMemo(
    () => rows.slice(indexOfFirstItem, indexOfLastItem),
    [rows, indexOfFirstItem, indexOfLastItem]
  );

  const fieldClass = useMemo(
    () =>
      `w-full max-w-full min-w-0 box-border px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
        isDark
          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block mb-2 text-sm font-medium ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const tdClass = useMemo(
    () => `px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`,
    [isDark]
  );

  /* ===========================
     helpers
  =========================== */
  const setField = useCallback((name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  /* ===========================
     meta load
  =========================== */
  const loadMeta = useCallback(
    async (selectedBranchId) => {
      try {
        setDropdownLoading(true);

        const { data } = await axios.get(`${API_BASE}/meta`, {
          params: {
            branchType: session.branchType,
            branchId: session.branchId,
            loginUserId: session.loginUserId,
            selectedBranchId:
              selectedBranchId ??
              filters.selectedBranchId ??
              defaultFilters.selectedBranchId,
          },
          headers: commonHeaders,
        });

        if (!data?.success) {
          throw new Error(data?.message || "Failed to load meta");
        }

        setMeta({
          branches: normalizeOptionList(data.branches, "Select", "Select"),
          batteryCodes: normalizeOptionList(data.batteryCodes, "Select", "Select"),
          batteryTypes: normalizeOptionList(data.batteryTypes, "Select", "Select"),
          serialNos: normalizeOptionList(data.serialNos, "Select", "Select"),
          vehicleMainTypes: normalizeOptionList(data.vehicleMainTypes, "Select", "Select"),
          batteryStatuses: normalizeOptionList(data.batteryStatuses, "Select", "Select"),
          vehicleStatuses: normalizeOptionList(data.vehicleStatuses, "Select", "Select"),
        });
      } catch (e) {
        console.error(e);
        toast.error(e?.response?.data?.message || e.message || "Failed to load dropdowns");

        setMeta({
          branches: [{ value: "Select", label: "Select" }],
          batteryCodes: [{ value: "Select", label: "Select" }],
          batteryTypes: [{ value: "Select", label: "Select" }],
          serialNos: [{ value: "Select", label: "Select" }],
          vehicleMainTypes: [{ value: "Select", label: "Select" }],
          batteryStatuses: [{ value: "Select", label: "Select" }],
          vehicleStatuses: [{ value: "Select", label: "Select" }],
        });
      } finally {
        setDropdownLoading(false);
      }
    },
    [commonHeaders, session, filters.selectedBranchId, defaultFilters.selectedBranchId]
  );

  /* ===========================
     list fetch
  =========================== */
  const fetchList = useCallback(
    async (nextFilters = filters) => {
      try {
        setListLoading(true);

        const { data } = await axios.get(`${API_BASE}/list`, {
          params: {
            branchType: session.branchType,
            selectedBranchId: nextFilters.selectedBranchId,
            loginUserId: session.loginUserId,

            fromDate: nextFilters.fromDate,
            toDate: nextFilters.toDate,

            batteryCode: nextFilters.batteryCode,
            batteryType: nextFilters.batteryType,
            batterySerialNo: nextFilters.batterySerialNo,

            store: nextFilters.store,

            vehicleCode: nextFilters.vehicleCode,
            vehicleName: nextFilters.vehicleName,
            vehicleRegNo: nextFilters.vehicleRegNo,

            batteryStatus: nextFilters.batteryStatus,
            vehicleMainTypeIndex: nextFilters.vehicleMainTypeIndex,
            vehicleStatus: nextFilters.vehicleStatus,
          },
          headers: commonHeaders,
        });

        if (!data?.success) {
          throw new Error(data?.message || "Failed to load report");
        }

        setRows(toList(data.rows));
        setPage(1);
      } catch (e) {
        console.error(e);
        toast.error(e?.response?.data?.message || e.message || "Failed to load report");
        setRows([]);
        setPage(1);
      } finally {
        setListLoading(false);
      }
    },
    [filters, session, commonHeaders]
  );

  /* ===========================
     initial load
  =========================== */
  const loadPage = useCallback(async () => {
    try {
      setPageLoading(true);

      setFilters(defaultFilters);
      setRows([]);
      setPage(1);

      await loadMeta(defaultFilters.selectedBranchId);
    } finally {
      setPageLoading(false);
    }
  }, [defaultFilters, loadMeta]);

  /* ===========================
     handlers
  =========================== */
  const handleChange = useCallback(
    async (e) => {
      const { name, value } = e.target;

      if (name === "selectedBranchId") {
        const next = {
          ...filters,
          selectedBranchId: value,
          batteryCode: "Select",
          batteryType: "Select",
          batterySerialNo: "Select",
        };

        setFilters(next);
        await loadMeta(value);
        return;
      }

      setField(name, value);
    },
    [filters, loadMeta, setField]
  );

  const handleSearch = useCallback(async () => {
    await fetchList(filters);
    toast.success("Report loaded");
  }, [fetchList, filters]);

  const handleViewAll = useCallback(async () => {
    const reset = createDefaultFilters(session.branchType, session.branchId);
    setFilters(reset);
    await loadMeta(reset.selectedBranchId);
    await fetchList(reset);
    toast.info("Showing all records");
  }, [session.branchType, session.branchId, loadMeta, fetchList]);

  const handleRefresh = useCallback(async () => {
    await loadPage();
    await fetchList(defaultFilters);
    toast.info("Page refreshed");
  }, [loadPage, fetchList, defaultFilters]);

  const handleReset = useCallback(async () => {
    const reset = createDefaultFilters(session.branchType, session.branchId);
    setFilters(reset);
    setRows([]);
    setPage(1);
    await loadMeta(reset.selectedBranchId);
    toast.info("Filters reset. Click Search to load.");
  }, [session.branchType, session.branchId, loadMeta]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handlePrintRow = useCallback(
    (row) => {
      const batteryId =
        row?.fld_batteryid || row?.batteryId || row?.fld_batterycardmainid;

      navigate("/vehicle/BatetryCardMaintenanceReport_Print", {
        state: { batteryId },
      });
    },
    [navigate]
  );

  const handleAllPrint = useCallback(() => {
    navigate("/vehicle/BatteryCardMaintenanceReport_AllPrint", {
      state: { filters },
    });
    toast.info("Printing all battery card records");
  }, [navigate, filters]);

  const handleExport = useCallback(() => {
    try {
      const headers = [
        "SNo",
        "BranchName",
        "VehicleMainType",
        "VehicleName",
        "VehicleRegNo",
        "BatteryCode",
        "BatteryType",
        "BatterySerialNo",
        "FittedDate",
        "CurrentDate",
        "ActualLife",
        "CurrentLife",
        "PerDayCost",
        "Alert",
      ];

      const lines = [headers.join(",")];

      rows.forEach((r, idx) => {
        const values = [
          idx + 1,
          toStr(r.fld_BranchName ?? r.branchname ?? r.branch ?? ""),
          toStr(r.VehicleType ?? r.vehicleMainType ?? ""),
          toStr(r.fld_Vehiclename ?? r.fld_vehicle_name ?? r.vehicleName ?? ""),
          toStr(r.fld_vehicle_regno ?? r.vehicleRegNo ?? ""),
          toStr(r.fld_batterycode ?? r.batteryCode ?? ""),
          toStr(r.fld_batterytype ?? r.batteryType ?? ""),
          toStr(r.fld_battery_serialno ?? r.batterySerialNo ?? ""),
          toStr(r.fld_Fitteddate ?? r.fittedDate ?? ""),
          toStr(r.currentdate ?? r.removeDate ?? ""),
          toStr(r.fld_actuallife ?? ""),
          toStr(r.fld_currentlife ?? ""),
          toStr(r.fld_perdaycost ?? ""),
          toStr(r.alert ?? ""),
        ].map((x) => `"${String(x).replace(/"/g, '""')}"`);

        lines.push(values.join(","));
      });

      const blob = new Blob([lines.join("\n")], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "BatteryCardMaintenanceReport.csv";
      a.click();
      URL.revokeObjectURL(url);

      toast.success("Exported CSV");
    } catch (e) {
      console.error(e);
      toast.error("Export failed");
    }
  }, [rows]);

  const goPage = useCallback(
    (p) => {
      const next = Math.min(Math.max(1, p), totalPages);
      setPage(next);
    },
    [totalPages]
  );

  /* ===========================
     effects
  =========================== */
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;

    (async () => {
      await loadPage();
      await fetchList(defaultFilters);
    })();
  }, [loadPage, fetchList, defaultFilters]);

  const statusText = pageLoading
    ? "Loading page..."
    : dropdownLoading
    ? "Loading dropdowns..."
    : listLoading
    ? "Searching..."
    : "Ready";

  return (
    <VehicleLayoutWrapper>
      <div
        className={`w-full max-w-full min-w-0 overflow-x-hidden box-border min-h-screen p-4 md:p-6 ${
          isDark ? "bg-gray-900" : "bg-gray-100"
        }`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-2xl md:text-3xl">
              BATTERY CARD MAINTENANCE REPORT
            </span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`w-full max-w-full min-w-0 overflow-hidden p-4 md:p-6 mt-6 rounded-2xl shadow-xl border box-border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div className="min-w-0">
              <h2 className={`text-xl md:text-2xl font-bold break-words ${isDark ? "text-white" : "text-gray-900"}`}>
                Battery Card Maintenance Report
              </h2>
              <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Branch ID: {session.branchId || "-"} • User ID: {session.loginUserId || "-"}
              </p>
            </div>

            <div className="flex items-center gap-2 md:gap-3 flex-wrap">
              <button
                onClick={handleAllPrint}
                disabled={isBusy || rows.length === 0}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white ${
                  isBusy || rows.length === 0 ? "opacity-60 cursor-not-allowed" : ""
                }`}
              >
                <FaPrint />
                Print
              </button>

              <button
                onClick={handleExport}
                disabled={isBusy || rows.length === 0}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                } ${isBusy || rows.length === 0 ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaFileExport />
                Export
              </button>

              <button
                onClick={handleRefresh}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaSyncAlt />
                Refresh
              </button>

              <button
                onClick={handleBack}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaArrowLeft />
                Back
              </button>

              <button
                onClick={() => setShowFilters((p) => !p)}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ==================== SEARCH FILTERS ==================== */}
          {showFilters && (
            <div className={`p-4 md:p-6 rounded-lg mb-6 overflow-hidden ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  Search Criteria
                </h3>

                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={handleReset}
                    type="button"
                    disabled={isBusy}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    Clear
                  </button>

                  <button
                    onClick={handleViewAll}
                    type="button"
                    disabled={isBusy}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    View All
                  </button>

                  <button
                    onClick={handleSearch}
                    type="button"
                    disabled={isBusy}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      isBusy ? "opacity-60 cursor-not-allowed" : ""
                    } ${buttonClass} ${hoverClass} text-white`}
                  >
                    <FaSearch />
                    {listLoading ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>

              {/* 3 columns × 4 rows */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div className="min-w-0">
                  <label className={labelClass}>From Date</label>
                  <input
                    type="date"
                    name="fromDate"
                    value={filters.fromDate}
                    onChange={handleChange}
                    className={fieldClass}
                    disabled={isBusy}
                  />
                </div>

                <div className="min-w-0">
                  <label className={labelClass}>To Date</label>
                  <input
                    type="date"
                    name="toDate"
                    value={filters.toDate}
                    onChange={handleChange}
                    className={fieldClass}
                    disabled={isBusy}
                  />
                </div>

                <div className="min-w-0">
                  <label className={labelClass}>Branch</label>
                  <select
                    name="selectedBranchId"
                    value={filters.selectedBranchId}
                    onChange={handleChange}
                    className={fieldClass}
                    disabled={isBusy || session.branchType !== "HO"}
                  >
                    {meta.branches.map((b, i) => (
                      <option key={`${b.value}-${i}`} value={b.value}>
                        {b.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Code</label>
                  <input
                    type="text"
                    name="vehicleCode"
                    value={filters.vehicleCode}
                    onChange={handleChange}
                    className={fieldClass}
                    placeholder="Enter vehicle code"
                    disabled={isBusy}
                  />
                </div>

                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Reg No</label>
                  <input
                    type="text"
                    name="vehicleRegNo"
                    value={filters.vehicleRegNo}
                    onChange={handleChange}
                    className={fieldClass}
                    placeholder="Enter registration number"
                    disabled={isBusy}
                  />
                </div>

                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Name</label>
                  <input
                    type="text"
                    name="vehicleName"
                    value={filters.vehicleName}
                    onChange={handleChange}
                    className={fieldClass}
                    placeholder="Enter vehicle name"
                    disabled={isBusy}
                  />
                </div>

                <div className="min-w-0">
                  <label className={labelClass}>Battery Code</label>
                  <select
                    name="batteryCode"
                    value={filters.batteryCode}
                    onChange={handleChange}
                    className={fieldClass}
                    disabled={isBusy}
                  >
                    {meta.batteryCodes.map((x, i) => (
                      <option key={`${x.value}-${i}`} value={x.label || x.value}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="min-w-0">
                  <label className={labelClass}>Battery Type</label>
                  <select
                    name="batteryType"
                    value={filters.batteryType}
                    onChange={handleChange}
                    className={fieldClass}
                    disabled={isBusy}
                  >
                    {meta.batteryTypes.map((x, i) => (
                      <option key={`${x.value}-${i}`} value={x.value}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="min-w-0">
                  <label className={labelClass}>Battery Serial No</label>
                  <select
                    name="batterySerialNo"
                    value={filters.batterySerialNo}
                    onChange={handleChange}
                    className={fieldClass}
                    disabled={isBusy}
                  >
                    {meta.serialNos.map((x, i) => (
                      <option key={`${x.value}-${i}`} value={x.value}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="min-w-0">
                  <label className={labelClass}>Battery Status</label>
                  <select
                    name="batteryStatus"
                    value={filters.batteryStatus}
                    onChange={handleChange}
                    className={fieldClass}
                    disabled={isBusy}
                  >
                    {meta.batteryStatuses.map((x, i) => (
                      <option key={`${x.value}-${i}`} value={x.value}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Main Type</label>
                  <select
                    name="vehicleMainTypeIndex"
                    value={filters.vehicleMainTypeIndex}
                    onChange={handleChange}
                    className={fieldClass}
                    disabled={isBusy}
                  >
                    {meta.vehicleMainTypes.map((x, i) => (
                      <option key={`${x.value}-${i}`} value={x.value}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Status</label>
                  <select
                    name="vehicleStatus"
                    value={filters.vehicleStatus}
                    onChange={handleChange}
                    className={fieldClass}
                    disabled={isBusy}
                  >
                    {meta.vehicleStatuses.map((x, i) => (
                      <option key={`${x.value}-${i}`} value={x.value}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* ==================== RESULTS SUMMARY ==================== */}
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
              }`}
            >
              Total Records: {totalRows}
            </div>
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              {statusText}
            </div>
          </div>

          {/* ==================== RESULTS TABLE ==================== */}
          <div className="w-full max-w-full overflow-x-auto rounded-lg border shadow-lg">
            {pageLoading || dropdownLoading || listLoading ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Loading...
                </div>
              </div>
            ) : currentItems.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  No Battery Records Found
                </div>
                <p className={`mt-2 text-sm ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                  Try adjusting your search criteria or click View All.
                </p>
              </div>
            ) : (
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold">S.No</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Vehicle Name</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Vehicle Reg No</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Battery Code</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Battery Serial No</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Fitted Date</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Actual Life</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Current Life</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Alert</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Print</th>
                  </tr>
                </thead>

                <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                  {currentItems.map((r, idx) => {
                    const sno = indexOfFirstItem + idx + 1;

                    const vehicleName =
                      r.fld_Vehiclename ?? r.fld_vehicle_name ?? r.vehicleName ?? "";

                    const regNo = r.fld_vehicle_regno ?? r.vehicleRegNo ?? "";
                    const batteryCode = r.fld_batterycode ?? r.batteryCode ?? "";
                    const serialNo = r.fld_battery_serialno ?? r.batterySerialNo ?? "";
                    const fittedDate = fmtDate(r.fld_Fitteddate ?? r.fittedDate ?? "");
                    const actualLife = r.fld_actuallife ?? r.actualLife ?? "";
                    const currentLife = r.fld_currentlife ?? r.currentLife ?? "";
                    const alert = r.alert ?? "";

                    const alertClass =
                      alert === "Need To Change"
                        ? isDark
                          ? "text-red-400"
                          : "text-red-600"
                        : alert === "Check Soon"
                        ? isDark
                          ? "text-yellow-400"
                          : "text-yellow-600"
                        : isDark
                        ? "text-green-400"
                        : "text-green-600";

                    return (
                      <tr
                        key={`${r.fld_batteryid ?? r.id ?? "row"}-${sno}`}
                        className={`${
                          idx % 2 === 0
                            ? isDark
                              ? "bg-gray-800"
                              : "bg-white"
                            : isDark
                            ? "bg-gray-750"
                            : "bg-gray-50"
                        } transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                      >
                        <td className={tdClass}>{sno}</td>
                        <td className={`px-4 py-3 text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
                          {vehicleName || "-"}
                        </td>
                        <td className={`px-4 py-3 text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                          {regNo || "-"}
                        </td>
                        <td className={tdClass}>{batteryCode || "-"}</td>
                        <td className={tdClass}>{serialNo || "-"}</td>
                        <td className={tdClass}>{fittedDate || "-"}</td>
                        <td className={tdClass}>{actualLife || "-"}</td>
                        <td className={tdClass}>{currentLife || "-"}</td>
                        <td className={`px-4 py-3 text-sm font-semibold ${alertClass}`}>
                          {alert || "-"}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handlePrintRow(r)}
                            disabled={isBusy}
                            className={`p-2 rounded transition ${
                              isDark
                                ? "text-white hover:bg-gray-600"
                                : "text-gray-800 hover:bg-gray-200"
                            } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                            title="Print"
                            type="button"
                          >
                            <FaPrint className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* ==================== PAGINATION ==================== */}
          {totalRows > 0 && (
            <div className="mt-6 flex justify-center items-center gap-3 flex-wrap">
              <button
                className={`px-4 py-2 border rounded-lg font-semibold ${
                  isDark
                    ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                    : "border-gray-300 text-gray-700 hover:bg-gray-100"
                } ${isBusy || page <= 1 ? "opacity-50 cursor-not-allowed" : ""}`}
                onClick={() => goPage(page - 1)}
                disabled={isBusy || page <= 1}
                type="button"
              >
                Prev
              </button>

              <div
                className={`px-4 py-2 border rounded-lg ${
                  isDark ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"
                }`}
              >
                Page {page} / {totalPages}
              </div>

              <button
                className={`px-4 py-2 border rounded-lg font-semibold ${
                  isDark
                    ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                    : "border-gray-300 text-gray-700 hover:bg-gray-100"
                } ${isBusy || page >= totalPages ? "opacity-50 cursor-not-allowed" : ""}`}
                onClick={() => goPage(page + 1)}
                disabled={isBusy || page >= totalPages}
                type="button"
              >
                Next
              </button>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={2500}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}